import { Label } from "layout/typography/Label";
import { Checkbox } from "primereact/checkbox";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";

const Title = styled.h3`
  margin-bottom: 10px;
  margin-left: 9px;
`;

const FlagDescription = styled.span`
  font-size: 12px;
  color: #6c757d;
  margin-left: 8px;
`;

interface IFeatureFlagItem {
  name: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export function FeatureFlags() {
  const { control, setValue, watch } = useFormContext<any>();

  // Watch the mutually exclusive cash flags
  const markPaidViaCashUnmanaged = watch("features.markPaidViaCashUnmanaged");
  const useFinix = watch("features.useFinix");

  // Order: Left column gets items 0, 2, 4 and right column gets items 1, 3, 5
  // So we interleave: left1, right1, left2, right2, left3, right3
  const featureFlags: IFeatureFlagItem[] = [
    {
      name: "features.markPaidViaCash",
      label: "Legacy Cash Payments",
      description:
        "Allow marking orders paid via cash (Clear Health handles transfers)",
    },
    {
      name: "features.orderPaymentNotesEnabled",
      label: "Order Payment Notes",
      description: "Enable payment notes on orders",
    },
    {
      name: "features.markPaidViaCashUnmanaged",
      label: "Unmanaged Cash Payments",
      description:
        "Allow marking orders paid via cash (account handles distribution internally)",
    },
    {
      name: "features.hstImport",
      label: "HST Import",
      description: "Enable HST data import",
    },
    {
      name: "features.athenaImport",
      label: "Athena Import",
      description: "Enable Athena scheduling report import",
    },
    {
      name: "features.markPaidViaCheck",
      label: "Check Payments",
      description: "Allow marking orders paid via check",
    },
    {
      name: "features.implantSuggestions",
      label: "Implant Suggestions",
      description: "Show implant CPT code suggestions",
    },
    {
      name: "features.checkoutContinueButtonVisible",
      label: "Checkout Continue Button",
      description: "Show the continue button on checkout",
    },
    {
      name: "features.useFinix",
      label: "Finix Payments",
      description:
        "Use Finix as the payment processor for this account (requires provider onboarding)",
    },
  ];

  // Handle mutual exclusivity for cash payment flags
  const handleCashFlagChange = (
    fieldName: string,
    value: boolean,
    onChange: (value: boolean) => void
  ) => {
    onChange(value);

    // When enabling CashUnmanaged, disable legacy Cash
    if (fieldName === "features.markPaidViaCashUnmanaged" && value) {
      setValue("features.markPaidViaCash", false);
    }
    // When enabling legacy Cash, disable CashUnmanaged
    if (fieldName === "features.markPaidViaCash" && value) {
      setValue("features.markPaidViaCashUnmanaged", false);
    }
  };

  return (
    <>
      <Title>Feature Flags</Title>
      <div className="flex flex-wrap w-100 mt-4 mb-4">
        {featureFlags.map((flag) => (
          <div key={flag.name} className="field-checkbox col-12 md:col-6 mb-3">
            <Controller
              name={flag.name}
              control={control}
              render={({ field: { onChange, value } }) => (
                <div className="flex align-items-start">
                  <Checkbox
                    inputId={flag.name}
                    checked={value || false}
                    onChange={(e) => {
                      if (
                        flag.name === "features.markPaidViaCash" ||
                        flag.name === "features.markPaidViaCashUnmanaged"
                      ) {
                        handleCashFlagChange(
                          flag.name,
                          e.checked || false,
                          onChange
                        );
                      } else {
                        onChange(e.checked);
                      }
                    }}
                    disabled={flag.disabled}
                  />
                  <div className="ml-2">
                    <Label htmlFor={flag.name}>{flag.label}</Label>
                    {flag.description && (
                      <FlagDescription className="block">
                        {flag.description}
                      </FlagDescription>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        ))}

        {/* Display warning when cash unmanaged is enabled */}
        {markPaidViaCashUnmanaged && (
          <div className="col-12 mt-2 mb-2">
            <div
              style={{
                backgroundColor: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: "4px",
                padding: "0.75rem",
                color: "#856404",
              }}
            >
              <strong>Warning:</strong> Unmanaged Cash Payments are enabled.
              Orders marked as paid via this method will NOT be processed by
              Clear Health and will NOT appear on weekly payment reports.
            </div>
          </div>
        )}

        {useFinix && (
          <div className="col-12 mt-2 mb-2">
            <div
              style={{
                backgroundColor: "#cff4fc",
                border: "1px solid #6edff6",
                borderRadius: "4px",
                padding: "0.75rem",
                color: "#055160",
              }}
            >
              <strong>Note:</strong> Finix payments are enabled for this
              account. Ensure all providers are onboarded before patients
              attempt to pay.
            </div>
          </div>
        )}
      </div>
    </>
  );
}
